import classnames from 'classnames';
import { FieldState, FormState } from 'formstate';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { lazyInject } from 'src/ioc';
import { UIStatusStore } from '../../stores/ui-status.store';
import { ContactUsApiService } from 'src/api';
import { BusyFormState } from 'src/util/busy-form';
import { ClientDataStore } from '../../stores/client-data.store';

import styles from './contact-us-dialog.less';

export class ContactUsFormState extends BusyFormState {
    text = new FieldState('').validators((value) => !value && 'Required');

    form = new FormState({
        text: this.text,
        isBusy: this.isBusy,
    });

    constructor() {
        super();
        this.form.validate();
    }
}

@observer
export class ContactUsDialog extends React.Component {
    @lazyInject(ClientDataStore) private clientData!: ClientDataStore;
    @lazyInject(UIStatusStore) private uiStatus!: UIStatusStore;
    @lazyInject(ContactUsApiService) private contactUsApi!: ContactUsApiService;

    @observable data: ContactUsFormState = new ContactUsFormState();
    @observable success = false;
    @observable error: string | undefined;

    @computed get phone() {
        return this.clientData.portalData ? this.clientData.portalData.brandData.phone : undefined;
    }

    @computed get email() {
        return this.clientData.portalData?.brandData?.email;
    }

    @computed get contactViaMessageEnabled() {
        // https://servicetitan.atlassian.net/browse/CPO-3243 - disable contact via message
        return false;
    }

    handleClose = () => {
        this.uiStatus.isContactUsOpen = false;
        setTimeout(() => {
            this.success = false;
            this.error = undefined;
        }, 500);
    };

    handleSubmit = () => {
        this.success = false;
        this.error = undefined;
        return this.data
            .withBusy(this.contactUsApi.sendContactUsEmail({ text: this.data.text.$ }))
            .then(
                () => {
                    this.success = true;
                    setTimeout(this.handleClose, 2000);
                },
                (err) => {
                    this.error = err;
                }
            );
    };

    handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.data.text.onChange(e.target.value);
    };

    render() {
        return (
            <Dialog
                open={this.uiStatus.isContactUsOpen}
                onClose={this.handleClose}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                aria-labelledby="contact-us-dialog-title"
            >
                <DialogTitle
                    id="contact-us-dialog-title"
                    disableTypography
                    className={styles.title}
                >
                    Contact Us
                </DialogTitle>
                <DialogContent>
                    <DialogContentText className={styles.phone}>
                        Email: <a href={`mailto:${this.email}`}>{this.email}</a>
                    </DialogContentText>
                    {this.contactViaMessageEnabled && (
                        <textarea
                            value={this.data.text.value}
                            onChange={this.handleTextChange}
                            className={styles.textarea}
                            autoFocus
                            placeholder="Let us know how we can help"
                            disabled={this.data.isBusy.$ || this.success}
                        />
                    )}
                    <DialogContentText className={styles.phone}>
                        You can also call: <a href={`tel:${this.phone}`}>{this.phone}</a>
                    </DialogContentText>
                </DialogContent>
                {this.contactViaMessageEnabled && (
                    <DialogActions>
                        <Button
                            onClick={this.handleSubmit}
                            color="primary"
                            disabled={this.data.form.hasError || this.success}
                            className={styles.submitButton}
                        >
                            Send
                        </Button>
                    </DialogActions>
                )}
                <button className={styles.close} onClick={this.handleClose} />
                {this.error ? (
                    <div className={classnames(styles.error, styles.message)}>
                        <img src="/assets/images/error-exclamation.svg" />
                        Message not delivered
                    </div>
                ) : null}
                {this.success ? (
                    <div className={classnames(styles.success, styles.message)}>
                        <img src="/assets/images/confirmation-check.svg" />
                        Message Sent
                    </div>
                ) : null}
            </Dialog>
        );
    }
}
